import React, { forwardRef } from 'react'

import { Editor } from '@tiptap/react'
import {
  TbAlignCenter,
  TbAlignJustified,
  TbAlignLeft,
  TbAlignRight,
  TbArrowBackUp,
  TbArrowForwardUp,
  TbBold,
  TbItalic,
  TbLink,
  TbLinkOff,
  TbList,
  TbListNumbers,
  TbStrikethrough,
  TbUnderline,
} from 'react-icons/tb'
import { twMerge } from 'tailwind-merge'

import Popover from '@/components/common/PopoverV2'
import { RICH_TEXT_AREA_NO_BLUR_CLASSNAME } from '@/components/common/TextArea/RichTextArea'
import { useRichTextAreaContext } from '@/components/common/TextArea/RichTextArea/context'
import {
  ContentTypeDropdown,
  ContentTypePopover,
  LinkEditorPanel,
  Toolbar,
} from '@/components/common/TextArea/RichTextArea/menus/TextMenu'
import {
  useTextMenuCommands,
  useTextMenuContentTypes,
  useTextMenuDisabledState,
  useTextMenuEditableState,
  useTextMenuState,
} from '@/components/common/TextArea/RichTextArea/menus/TextMenu/hooks'

interface ToolbarGroupProps {
  children: React.ReactNode
  className?: string
}

/**
 * What the toolbar gives up first as its own room runs out, in the order a note
 * can least afford to lose it: undo and the lists have keyboard shortcuts, and
 * the block type keeps its whole menu once its label collapses to an icon. Link
 * and align have no other way in, so they hold on longest.
 */
const COLLAPSE_CLASSNAMES = {
  history: 'hidden @[560px]/editor-tools:flex',
  blockTypeLabel: 'hidden @[500px]/editor-tools:block',
  blockTypeIcon: '@[500px]/editor-tools:hidden',
  gaps: 'gap-1 @[500px]/editor-tools:gap-[10px]',
  align: 'hidden @[380px]/editor-tools:flex',
  link: 'hidden @[260px]/editor-tools:flex',
  lists: 'hidden @[200px]/editor-tools:block',
}

interface BaseEditorToolbarProps {
  editor: Editor | null
  className?: string
  buttonClassName?: string
  contentDropdown?: 'popover' | 'dropdown'
  initialContentTypeLabel?: string
  // Drop groups as the bar narrows instead of scrolling them out of reach. The
  // toolbar has to be laid out as a flex child to have a width to measure.
  collapse?: boolean
}

interface EditorToolbarProps {
  className?: string
  buttonClassName?: string
  contentDropdown?: 'popover' | 'dropdown'
  initialContentTypeLabel?: string
}

const EditorButton = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Toolbar.Button>
>(({ className, children, ...props }, ref) => {
  return (
    <Toolbar.Button
      {...props}
      type="button"
      ref={ref}
      className={twMerge(
        '!rounded-[3px]',
        'bg-white dark:bg-primary-900',
        props.isActive && 'bg-primary-200 dark:bg-primary-800',
        className,
        RICH_TEXT_AREA_NO_BLUR_CLASSNAME,
      )}
    >
      {children}
    </Toolbar.Button>
  )
})

const ToolbarGroup: React.FC<ToolbarGroupProps> = ({ children, className }) => {
  return (
    <div
      className={twMerge(
        'flex items-center gap-x-[1px] px-[1px] rounded-[5px] h-7 border border-primary-300 dark:border-primary-700',
        className,
      )}
    >
      {children}
    </div>
  )
}

export const BaseEditorToolbar: React.FC<BaseEditorToolbarProps> = ({
  editor,
  className,
  buttonClassName,
  contentDropdown = 'dropdown',
  initialContentTypeLabel,
  collapse,
}) => {
  const editorState = useTextMenuState({ editor })
  const commands = useTextMenuCommands({ editor })
  const contentTypes = useTextMenuContentTypes({ editor })
  const disabledState = useTextMenuDisabledState({ editor })
  const editableState = useTextMenuEditableState({ editor })

  const collapseClassName = (group: keyof typeof COLLAPSE_CLASSNAMES) =>
    collapse ? COLLAPSE_CLASSNAMES[group] : undefined

  const contentTypeProps = {
    options: contentTypes ?? [],
    disabled: editor === null || !editableState.editable,
    initialLabel: initialContentTypeLabel,
  }

  const groups = (
    <div
      className={twMerge(
        'flex items-center',
        collapse ? COLLAPSE_CLASSNAMES.gaps : 'gap-[10px]',
        className,
      )}
    >
      <ToolbarGroup className={collapseClassName('history')}>
        <EditorButton
          tooltip="Undo"
          onClick={commands.onUndo}
          disabled={disabledState.canUndo}
          className={buttonClassName}
        >
          <TbArrowBackUp className="w-4 h-4" />
        </EditorButton>
        <EditorButton
          tooltip="Redo"
          onClick={commands.onRedo}
          disabled={disabledState.canRedo}
          className={buttonClassName}
        >
          <TbArrowForwardUp className="w-4 h-4" />
        </EditorButton>
      </ToolbarGroup>
      {contentDropdown === 'dropdown' ? (
        <ContentTypeDropdown {...contentTypeProps} />
      ) : collapse ? (
        // Two triggers rather than one that reflows: the named button is what
        // the room buys, and the icon opens the same menu without it.
        <>
          <div className={COLLAPSE_CLASSNAMES.blockTypeLabel}>
            <ContentTypePopover {...contentTypeProps} expandedTrigger={true} />
          </div>
          <ToolbarGroup className={COLLAPSE_CLASSNAMES.blockTypeIcon}>
            <ContentTypePopover
              {...contentTypeProps}
              triggerClassName={twMerge('!rounded-[3px]', buttonClassName)}
            />
          </ToolbarGroup>
        </>
      ) : (
        <ContentTypePopover {...contentTypeProps} expandedTrigger={true} />
      )}
      <ToolbarGroup>
        <EditorButton
          isActive={editorState.isBold}
          tooltip="Bold"
          onClick={commands.onBold}
          disabled={disabledState.canBold}
          className={buttonClassName}
        >
          <TbBold className="w-4 h-4" />
        </EditorButton>
        <EditorButton
          isActive={editorState.isItalic}
          tooltip="Italic"
          onClick={commands.onItalic}
          disabled={disabledState.canItalic}
          className={buttonClassName}
        >
          <TbItalic className="w-4 h-4" />
        </EditorButton>
        <EditorButton
          isActive={editorState.isUnderline}
          tooltip="Underline"
          onClick={commands.onUnderline}
          disabled={disabledState.canUnderline}
          className={buttonClassName}
        >
          <TbUnderline className="w-4 h-4" />
        </EditorButton>
        <EditorButton
          isActive={editorState.isStrike}
          tooltip="Strikethrough"
          onClick={commands.onStrike}
          disabled={disabledState.canStrike}
          className={buttonClassName}
        >
          <TbStrikethrough className="w-4 h-4" />
        </EditorButton>
        <EditorButton
          isActive={editorState.isBulletList}
          tooltip="Bulleted list"
          onClick={commands.onBulletList}
          disabled={disabledState.canBulletList}
          className={twMerge(buttonClassName, collapseClassName('lists'))}
        >
          <TbList className="w-4 h-4" />
        </EditorButton>
        <EditorButton
          isActive={editorState.isOrderedList}
          tooltip="Numbered list"
          onClick={commands.onOrderedList}
          disabled={disabledState.canOrderedList}
          className={twMerge(buttonClassName, collapseClassName('lists'))}
        >
          <TbListNumbers className="w-4 h-4" />
        </EditorButton>
      </ToolbarGroup>
      <ToolbarGroup className={collapseClassName('link')}>
        <Popover>
          <Popover.Trigger asChild={true}>
            <EditorButton
              aria-label="Add link"
              disabled={disabledState.canSetLink}
              className={buttonClassName}
            >
              <TbLink className="w-4 h-4" />
            </EditorButton>
          </Popover.Trigger>
          <Popover.Content className="p-2">
            <LinkEditorPanel onSetLink={commands.onSetLink} />
          </Popover.Content>
        </Popover>
        <EditorButton
          tooltip="Remove link"
          onClick={commands.onUnsetLink}
          disabled={disabledState.canUnsetLink}
          className={buttonClassName}
        >
          <TbLinkOff className="w-4 h-4" />
        </EditorButton>
      </ToolbarGroup>
      <ToolbarGroup className={collapseClassName('align')}>
        <EditorButton
          isActive={editorState.isAlignLeft}
          tooltip="Left align"
          onClick={commands.onAlignLeft}
          disabled={disabledState.canAlignLeft}
          className={buttonClassName}
        >
          <TbAlignLeft className="w-4 h-4" />
        </EditorButton>
        <EditorButton
          isActive={editorState.isAlignCenter}
          tooltip="Center align"
          onClick={commands.onAlignCenter}
          disabled={disabledState.canAlignCenter}
          className={buttonClassName}
        >
          <TbAlignCenter className="w-4 h-4" />
        </EditorButton>
        <EditorButton
          isActive={editorState.isAlignRight}
          tooltip="Right align"
          onClick={commands.onAlignRight}
          disabled={disabledState.canAlignRight}
          className={buttonClassName}
        >
          <TbAlignRight className="w-4 h-4" />
        </EditorButton>
        <EditorButton
          isActive={editorState.isAlignJustify}
          tooltip="Justify"
          onClick={commands.onAlignJustify}
          disabled={disabledState.canAlignJustify}
          className={buttonClassName}
        >
          <TbAlignJustified className="w-4 h-4" />
        </EditorButton>
      </ToolbarGroup>
    </div>
  )

  // A container query needs a container, and the one that matters is the room
  // the bar has left over once its other controls have taken theirs.
  return collapse ? (
    <div className="w-full min-w-0 flex-1 @container/editor-tools">
      {groups}
    </div>
  ) : (
    groups
  )
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  className,
  buttonClassName,
  contentDropdown = 'dropdown',
  initialContentTypeLabel,
}) => {
  const { editor } = useRichTextAreaContext()
  return (
    <BaseEditorToolbar
      editor={editor}
      className={className}
      buttonClassName={buttonClassName}
      contentDropdown={contentDropdown}
      initialContentTypeLabel={initialContentTypeLabel}
    />
  )
}

EditorButton.displayName = 'RichTextArea.Toolbar.EditorButton'
ToolbarGroup.displayName = 'RichTextArea.Toolbar.ToolbarGroup'
EditorToolbar.displayName = 'RichTextArea.Toolbar.EditorToolbar'
